import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowRight, Bug, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_CONTROL_SELECT_OPEN_CLASS,
} from '@/components/ui/form-control-styles'
import { Label } from '@/components/ui/label'
import { EntityPicker } from '@/components/shared/EntityPicker'
import { ClientNameWithProspect } from '@/components/shared/StatusBadges'
import type { AttachmentRef } from '@/components/shared'
import { useCreatePayment } from '@/hooks/usePayments'
import { useJob } from '@/hooks/useJobs'
import { clientsApi } from '@/services/clients'
import { getApiErrorMessage } from '@/services/api'
import { invoicesApi } from '@/services/invoices'
import { jobsApi } from '@/services/jobs'
import { paymentsApi } from '@/services/payments'
import { toast } from '@/components/ui/toast'
import { cn } from '@/lib/utils'
import {
  formatCurrency,
  formatCurrencyInputValue,
  parseCurrencyInput,
} from '@/lib/format'
import {
  buildPaymentProofItems,
  isPaymentProofFileSupported,
  isPaymentProofFileTooLarge,
  mergePaymentProofItems,
  renamePaymentProofItem,
  type PaymentProofPreviewFile,
} from '@/components/payments/paymentProofPreview'
import {
  PaymentFormFields,
  type PaymentFormErrors,
  type PaymentFormFieldsProps,
} from '@/components/payments/PaymentFormFields'
import { PaymentSection } from '@/components/payments/PaymentSection'
import { SectionCard } from '@/components/payments/PaymentCreateSectionCard'
import {
  INVOICE_STATUS_LABELS,
  type PaymentStatus,
  type Client,
  type CreatePaymentMethod,
  type CreatePaymentRequest,
  type Invoice,
  type Job,
  type PaymentMethod,
} from '@/types'

const pickerFieldClass = cn(
  'h-10 rounded-xl border-transparent bg-[#f4f5f8] py-3 font-sans text-sm font-normal leading-[1.4] text-[#171a20] [&>span]:font-normal data-[has-value=true]:[&>span]:font-normal [&>span>span]:font-normal data-[has-value=true]:[&>span>span]:font-normal',
  FORM_CONTROL_SELECT_OPEN_CLASS,
  FORM_CONTROL_FOCUS_CLASS
)

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function toPaymentAmount(value?: number | null): string {
  return typeof value === 'number' && value > 0
    ? formatCurrencyInputValue(value)
    : ''
}

function normalizePaymentAmountDraft(value: string): string {
  const parsed = parseCurrencyInput(value)
  return parsed !== null && parsed > 0
    ? formatCurrencyInputValue(parsed)
    : value
}

function invoiceOutstanding(invoice: Invoice): number {
  return Math.max((invoice.total || 0) - (invoice.amount_paid || 0), 0)
}

function defaultStatusForMethod(
  method: CreatePaymentMethod | '' | undefined
): PaymentStatus | '' {
  if (!method) return ''
  return method === 'card' || method === 'cheque' || method === 'transfer'
    ? 'pending'
    : 'paid'
}

function compactTime(value?: string | null): string | null {
  if (!value) return null
  const [hours = '', minutes = ''] = value.split(':')
  const hour = Number(hours)
  if (!Number.isFinite(hour)) return value
  const period = hour >= 12 ? 'pm' : 'am'
  const twelveHour = hour % 12 || 12
  return minutes === '00' || minutes === ''
    ? `${twelveHour}${period}`
    : `${twelveHour}:${minutes}${period}`
}

function timeSlotLabel(job?: Job | null): string | null {
  const start = compactTime(job?.start_time)
  const end = compactTime(job?.end_time)
  return start && end ? `${start} - ${end}` : null
}

function InterventionSection({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <section className='border-t border-[#e7e9ef] px-6 py-4'>
      <div className='mb-3 flex items-center gap-2'>
        <span className='h-4 w-1 rounded-full bg-[#ff5a1f]' />
        <h3 className='font-heading text-sm font-semibold uppercase leading-none text-[#171a20]'>
          {title}
        </h3>
      </div>
      {children}
    </section>
  )
}

function InterventionInfoRow({
  label,
  value,
}: {
  label: string
  value: string
}) {
  return (
    <div className='grid min-h-10 grid-cols-[136px_minmax(0,1fr)] items-center gap-3 border-b border-[#e7e9ef] px-4 py-2 last:border-b-0 last:pb-3'>
      <div className='text-sm font-medium leading-[1.5] text-[#3d434e]'>
        {label}
      </div>
      <div className='min-w-0 text-right text-sm font-medium leading-[1.5] text-[#171a20]'>
        {value}
      </div>
    </div>
  )
}

function InterventionInformationSection({
  client,
  job,
  invoice,
}: {
  client: string
  job: string
  invoice: string
}) {
  const { t } = useTranslation('payments')

  return (
    <InterventionSection title={t('form.information')}>
      <div className='overflow-hidden rounded-2xl bg-[#f4f5f8]'>
        <InterventionInfoRow label={t('form.client')} value={client} />
        <InterventionInfoRow label={t('form.linkedJob')} value={job} />
        <InterventionInfoRow label={t('form.linkedInvoice')} value={invoice} />
      </div>
    </InterventionSection>
  )
}

type Props = {
  initialClientId?: string
  initialJobId?: string
  initialInvoiceId?: string
  initialInvoice?: Invoice | null
  initialAmount?: string
  reportJob?: string
  presentation?: 'default' | 'intervention'
  variant?: 'drawer' | 'inline'
  hideContextFields?: boolean
  showStatusField?: boolean
  showCancelAction?: boolean
  onCancel: () => void
  onRecorded: (paymentId: string) => void
  onReportRequested?: (jobId: string) => void
}

function PaymentCreateFormCore({
  initialClientId = '',
  initialJobId = '',
  initialInvoiceId = '',
  initialInvoice = null,
  initialAmount = '',
  reportJob = '',
  presentation = 'default',
  variant = 'drawer',
  hideContextFields = false,
  showStatusField = true,
  showCancelAction = true,
  onCancel,
  onRecorded,
  onReportRequested,
}: Props) {
  const { t } = useTranslation('payments')
  const navigate = useNavigate()
  const create = useCreatePayment()
  const proofInputRef = useRef<HTMLInputElement>(null)
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(
    null
  )

  const initialContextClientId =
    initialClientId || initialInvoice?.client_id || ''
  const [clientId, setClientId] = useState(initialContextClientId)
  const [clientSelectionTouched, setClientSelectionTouched] = useState(
    initialContextClientId.length > 0
  )
  const [jobId, setJobId] = useState(initialJobId)
  const [invoiceId, setInvoiceId] = useState(
    initialInvoiceId || initialInvoice?.id || ''
  )
  const [linkedInvoice, setLinkedInvoice] = useState<Invoice | null>(
    initialInvoice
  )
  const [linkedJob, setLinkedJob] = useState<Job | null>(null)
  const [amount, setAmount] = useState(
    normalizePaymentAmountDraft(initialAmount) ||
      (initialInvoice
        ? toPaymentAmount(invoiceOutstanding(initialInvoice))
        : '')
  )
  const [method, setMethod] = useState<PaymentMethod | ''>('')
  const [status, setStatus] = useState<PaymentStatus | ''>('')
  const [paidDate, setPaidDate] = useState(today())
  const [reference, setReference] = useState('')
  const [proofs, setProofs] = useState<PaymentProofPreviewFile[]>([])
  const [proofDragActive, setProofDragActive] = useState(false)
  const proofPreviewItems = useMemo<AttachmentRef[]>(
    () =>
      proofs.map(item => ({
        id: item.id,
        name: item.file.name,
        size: item.file.size,
        mime_type: item.file.type,
        source: 'upload',
        file: item.file,
      })),
    [proofs]
  )
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [waiting, setWaiting] = useState<{ id: string; url: string } | null>(
    null
  )
  const [errors, setErrors] = useState<PaymentFormErrors>({})

  const { data: prefillJob } = useJob(initialJobId || undefined)
  const effectiveClientId = clientSelectionTouched
    ? clientId
    : clientId || prefillJob?.data?.client_id || ''
  const effectiveLinkedJob =
    linkedJob ??
    (jobId && jobId === initialJobId ? (prefillJob?.data ?? null) : null)
  const isInterventionPayment = presentation === 'intervention'
  const interventionJob = prefillJob?.data ?? effectiveLinkedJob
  const interventionInvoice = prefillJob?.data?.invoice ?? linkedInvoice
  const interventionClientName =
    interventionJob?.client?.name ??
    interventionJob?.client_name ??
    t('form.unavailable')
  const interventionClientType =
    interventionJob?.client?.type === 'individual'
      ? t('form.interventionClientIndividual')
      : t('form.interventionClientBusiness')
  const interventionService =
    interventionJob?.service_type ?? t('form.interventionServiceFallback')
  const interventionSubtitle = [
    interventionJob?.reference,
    interventionClientType,
    timeSlotLabel(interventionJob),
  ]
    .filter(Boolean)
    .join(' · ')
  const interventionJobLabel =
    [interventionJob?.reference, interventionJob?.service_type]
      .filter(Boolean)
      .join(' - ') || t('form.unavailable')
  const interventionInvoiceLabel = interventionInvoice
    ? `${interventionInvoice.number ?? t('form.invoiceFallback')} - ${formatCurrency(interventionInvoice.total, 'EUR', 2)}`
    : t('form.unavailable')

  const continueToReport = useCallback(() => {
    if (!reportJob) return false

    if (onReportRequested) onReportRequested(reportJob)
    else navigate(`/jobs/${reportJob}/report/new`)
    return true
  }, [navigate, onReportRequested, reportJob])

  useEffect(() => {
    if (!waiting) return
    const { id } = waiting
    let active = true

    const tick = async () => {
      try {
        const res = await paymentsApi.get(id)
        if (active && res.data?.status === 'paid') {
          setWaiting(null)
          if (!continueToReport()) onRecorded(id)
        }
      } catch {
        // Keep polling until timeout.
      }
    }

    const interval = setInterval(tick, 3000)
    const deadline = setTimeout(() => setWaiting(null), 5 * 60 * 1000)
    return () => {
      active = false
      clearInterval(interval)
      clearTimeout(deadline)
    }
  }, [continueToReport, onRecorded, waiting])

  const outstanding = useMemo(() => {
    if (!linkedInvoice) return null
    return (linkedInvoice.total || 0) - (linkedInvoice.amount_paid || 0)
  }, [linkedInvoice])

  const amountNum = parseCurrencyInput(amount)
  const overpayment =
    outstanding !== null &&
    amountNum !== null &&
    amountNum > 0 &&
    amountNum > outstanding
  const showReference = method === 'transfer' || method === 'cheque'
  const statusLocked = method === 'card'

  const validate = () => {
    const next: typeof errors = {}
    if (!effectiveClientId) next.client_id = t('form.validation.client')
    if (amountNum === null || amountNum <= 0) {
      next.amount = t('form.validation.amount')
    } else if (overpayment) {
      next.amount = t('form.validation.amountExceedsBalanceDue')
    }
    if (!method) next.method = t('form.validation.method')
    if (!status) next.status = t('updateStatusDialog.selectStatusError')
    if (!paidDate) next.paid_date = t('form.validation.paidDate')
    if (proofs.some(item => !isPaymentProofFileSupported(item.file))) {
      next.proof = t('form.validation.proofFormat')
    }
    if (proofs.some(item => isPaymentProofFileTooLarge(item.file))) {
      next.proof = t('form.validation.proofSize')
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const pickProofs = (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return

    const nextItems = buildPaymentProofItems(Array.from(files))
    const validItems: PaymentProofPreviewFile[] = []
    let proofError: string | undefined
    for (const item of nextItems) {
      if (!isPaymentProofFileSupported(item.file)) {
        proofError = t('form.validation.proofFormat')
        continue
      }
      if (isPaymentProofFileTooLarge(item.file)) {
        proofError = t('form.validation.proofSize')
        continue
      }
      validItems.push(item)
    }

    setErrors(prev => ({ ...prev, proof: proofError }))
    if (validItems.length > 0) {
      setProofs(prev => mergePaymentProofItems(prev, validItems))
    }
  }

  const save = async () => {
    if (!validate()) return
    setSaving(true)
    try {
      const body: CreatePaymentRequest = {
        client_id: effectiveClientId,
        invoice_id: invoiceId || undefined,
        job_id: jobId || undefined,
        amount: amountNum!,
        method: method as CreatePaymentMethod,
        status: status || undefined,
        paid_date: paidDate || undefined,
        reference_number: reference.trim() || undefined,
        note: notes || undefined,
      }

      const res = await create.mutateAsync(body)
      const payment = res.data

      if (payment?.id && proofs.length > 0) {
        try {
          await paymentsApi.uploadProof(
            payment.id,
            proofs.map(item => item.file)
          )
        } catch {
          toast({ variant: 'destructive', title: t('form.uploadProofError') })
        }
      }

      if (payment?.checkout_url && payment?.id) {
        const win = window.open(
          payment.checkout_url,
          '_blank',
          'noopener,noreferrer'
        )
        if (!win) {
          toast({ variant: 'destructive', title: t('form.stripePopupBlocked') })
        }
        setWaiting({ id: payment.id, url: payment.checkout_url })
        return
      }

      toast({ variant: 'success', title: t('form.recordSuccess') })
      if (continueToReport()) return
      if (payment?.id) onRecorded(payment.id)
      else onCancel()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error, t('form.saveError')),
      })
    } finally {
      setSaving(false)
    }
  }

  const paymentFieldsProps = {
    amount,
    method,
    status,
    paidDate,
    reference,
    proofInputRef,
    proofPreviewItems,
    proofDragActive,
    proofsError: errors.proof,
    notes,
    waiting,
    errors,
    overpayment,
    outstanding,
    showStatusField,
    statusLocked,
    showReference,
    portalContainer,
    onAmountChange: setAmount,
    onMethodChange: (value: PaymentMethod) => {
      setMethod(value)
      setStatus(defaultStatusForMethod(value as CreatePaymentMethod))
      setErrors(prev => ({
        ...prev,
        method: undefined,
        status: undefined,
      }))
    },
    onStatusChange: (value: PaymentStatus) => {
      setStatus(value)
      setErrors(prev => ({ ...prev, status: undefined }))
    },
    onPaidDateChange: setPaidDate,
    onReferenceChange: setReference,
    onPickProofs: pickProofs,
    onProofDragActiveChange: setProofDragActive,
    onRemoveProof: (removeId: string) =>
      setProofs(prev => prev.filter(file => file.id !== removeId)),
    onRenameProof: (item: AttachmentRef, name: string) => {
      const proofId = item.id
      if (!proofId) return
      setProofs(prev => renamePaymentProofItem(prev, proofId, name))
    },
    onNotesChange: setNotes,
  } satisfies Omit<PaymentFormFieldsProps, 'layout'>

  const actionBar = (
    <div
      className={cn(
        'flex items-center justify-end gap-2 bg-background',
        variant === 'inline'
          ? 'pt-4'
          : isInterventionPayment
            ? 'rounded-2xl border border-[#e7e9ef] py-4 pl-6 pr-4 shadow-[0_1px_3px_rgba(23,26,32,0.04)]'
            : 'tf-right-drawer-footer px-6'
      )}
    >
      {showCancelAction && (
        <Button
          type='button'
          variant='outline'
          className='h-10 rounded-xl border-[#e7e9ef] px-4 text-sm font-medium leading-none'
          onClick={onCancel}
        >
          {t('form.cancel')}
        </Button>
      )}
      <Button
        type='button'
        className={cn(
          'h-10 rounded-xl px-4 text-sm font-medium leading-none text-white',
          isInterventionPayment && 'gap-2',
          variant === 'inline' &&
            'h-auto gap-2 px-3 py-3 text-sm font-medium leading-none text-white'
        )}
        disabled={saving || !!waiting}
        onClick={save}
      >
        {variant === 'inline' && <Plus className='size-4' />}
        {variant === 'inline'
          ? t('form.createPayment')
          : t('form.recordPayment')}
        {isInterventionPayment && <ArrowRight className='size-4' />}
      </Button>
    </div>
  )

  return (
    <div
      ref={setPortalContainer}
      className={cn(
        'flex flex-col',
        variant === 'inline' ? 'bg-background' : 'h-full min-h-0 bg-[#F4F5F8]'
      )}
    >
      <div
        className={cn(
          variant === 'inline'
            ? 'p-0'
            : isInterventionPayment
              ? 'min-h-0 flex-1 overflow-y-auto p-6'
              : 'min-h-0 flex-1 overflow-y-auto p-4'
        )}
      >
        <div className='flex flex-col gap-4'>
          {isInterventionPayment ? (
            <>
              <div>
                <Button
                  type='button'
                  variant='ghost'
                  className='h-8 rounded-md bg-white px-2 text-xs font-medium text-[#171a20] shadow-sm hover:bg-white'
                  onClick={onCancel}
                >
                  <ArrowLeft className='mr-1 h-3.5 w-3.5' />
                  {t('form.backToIntervention')}
                </Button>
              </div>

              <div className='overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_3px_rgba(23,26,32,0.04)]'>
                <header className='px-5 py-4'>
                  <div className='mb-2 flex flex-wrap items-center gap-2'>
                    <span className='rounded-lg bg-[#D1FAE5] px-2 py-1 text-xs font-normal leading-none text-[#065F46]'>
                      {t('form.paymentBadge')}
                    </span>
                    <span className='inline-flex items-center gap-1 rounded-lg bg-[#eef2f7] px-2 py-1 text-xs font-normal leading-none text-[#171a20]'>
                      <Bug className='size-3' strokeWidth={1.8} />
                      {interventionService}
                    </span>
                  </div>
                  <h2 className='font-heading text-xl font-semibold leading-[1.2] text-[#171a20]'>
                    {t('form.interventionPaymentTitle')}
                  </h2>
                  {interventionSubtitle && (
                    <p className='mt-1 text-xs font-medium leading-[1.35] text-[#3d434e]'>
                      {interventionSubtitle}
                    </p>
                  )}
                </header>

                <InterventionInformationSection
                  client={interventionClientName}
                  job={interventionJobLabel}
                  invoice={interventionInvoiceLabel}
                />

                <InterventionSection title={t('form.payment')}>
                  <div className='flex flex-col gap-4'>
                    <PaymentFormFields
                      {...paymentFieldsProps}
                      layout='drawer'
                    />
                  </div>
                </InterventionSection>
              </div>
            </>
          ) : (
            <>
              {!hideContextFields && (
                <SectionCard index={1} title={t('form.information')}>
                  <div className='space-y-1.5'>
                    <Label className='text-xs font-medium leading-4 text-foreground'>
                      {t('form.client')}{' '}
                      <span className='text-destructive'>*</span>
                    </Label>
                    <div className='flex items-center gap-2'>
                      <div className='min-w-0 flex-1'>
                        <EntityPicker<Client>
                          queryKey='clients'
                          preload
                          placeholder={t('form.clientPlaceholder')}
                          fetcher={({ search, limit, page }) =>
                            clientsApi.list({ search, limit, page })
                          }
                          getOptionLabel={client => client.name}
                          getOptionValue={client => client.id}
                          renderOption={client => (
                            <ClientNameWithProspect
                              name={client.name}
                              category={client.category}
                            />
                          )}
                          renderValue={(label, item) => (
                            <ClientNameWithProspect
                              name={label}
                              category={
                                item?.category ??
                                prefillJob?.data?.client?.category
                              }
                            />
                          )}
                          value={effectiveClientId || null}
                          initialLabel={
                            prefillJob?.data?.client?.name ??
                            prefillJob?.data?.client_name ??
                            undefined
                          }
                          portalContainer={portalContainer}
                          onChange={value => {
                            setClientSelectionTouched(true)
                            setClientId(value)
                            setJobId('')
                            setInvoiceId('')
                            setLinkedJob(null)
                            setLinkedInvoice(null)
                            setErrors(prev => ({
                              ...prev,
                              client_id: undefined,
                            }))
                          }}
                          className={cn(
                            pickerFieldClass,
                            errors.client_id && 'border-destructive'
                          )}
                        />
                      </div>
                      <Button
                        type='button'
                        variant='outline'
                        className='h-10 rounded-xl border-dashed border-primary bg-background px-3 text-xs font-medium leading-none text-primary'
                        onClick={() => navigate('/clients/new')}
                      >
                        <Plus className='mr-1 h-4 w-4' />
                        {t('form.newCustomer')}
                      </Button>
                    </div>
                    {errors.client_id && (
                      <p className='text-xs text-destructive'>
                        {errors.client_id}
                      </p>
                    )}
                  </div>

                  <div className='space-y-1.5'>
                    <Label className='text-xs font-medium leading-4 text-foreground'>
                      {t('form.linkedJob')}
                    </Label>
                    <EntityPicker<Job>
                      key={`job-${effectiveClientId}`}
                      queryKey={`jobs-${effectiveClientId}`}
                      preload={!!effectiveClientId}
                      disabled={!effectiveClientId}
                      placeholder={
                        effectiveClientId
                          ? t('form.select')
                          : t('form.selectClientFirst')
                      }
                      fetcher={({ search, limit, page }) =>
                        jobsApi.list({
                          search,
                          limit,
                          page,
                          client_id: effectiveClientId,
                        })
                      }
                      getOptionLabel={job =>
                        `${job.reference ?? job.id.slice(0, 8)}${job.service_type ? ` - ${job.service_type}` : ''}`
                      }
                      getOptionValue={job => job.id}
                      value={jobId || null}
                      initialLabel={
                        effectiveLinkedJob
                          ? `${effectiveLinkedJob.reference ?? effectiveLinkedJob.id.slice(0, 8)}${effectiveLinkedJob.service_type ? ` - ${effectiveLinkedJob.service_type}` : ''}`
                          : undefined
                      }
                      portalContainer={portalContainer}
                      onChange={(value, item) => {
                        setJobId(value)
                        setLinkedJob(item)
                        if (item) {
                          setAmount(toPaymentAmount(item.amount_to_collect))
                          setErrors(prev => ({ ...prev, amount: undefined }))
                        }
                      }}
                      className={pickerFieldClass}
                    />
                  </div>

                  <div className='space-y-1.5'>
                    <Label className='text-xs font-medium leading-4 text-foreground'>
                      {t('form.linkedInvoice')}
                    </Label>
                    <EntityPicker<Invoice>
                      key={`inv-${effectiveClientId}`}
                      queryKey={`invoices-${effectiveClientId}`}
                      preload={!!effectiveClientId}
                      disabled={!effectiveClientId}
                      placeholder={
                        effectiveClientId
                          ? t('form.select')
                          : t('form.selectClientFirst')
                      }
                      fetcher={({ search, limit, page }) =>
                        invoicesApi.list({
                          search,
                          limit,
                          page,
                          client_id: effectiveClientId,
                        })
                      }
                      getOptionLabel={invoice =>
                        `${invoice.number ?? invoice.id.slice(0, 8)} - ${formatCurrency(invoice.total, 'EUR', 2)}`
                      }
                      getOptionValue={invoice => invoice.id}
                      value={invoiceId || null}
                      initialLabel={
                        linkedInvoice
                          ? `${linkedInvoice.number ?? linkedInvoice.id.slice(0, 8)} - ${formatCurrency(linkedInvoice.total, 'EUR', 2)}`
                          : undefined
                      }
                      portalContainer={portalContainer}
                      onChange={(value, item) => {
                        setInvoiceId(value)
                        setLinkedInvoice(item)
                        if (item) {
                          setAmount(toPaymentAmount(invoiceOutstanding(item)))
                          setErrors(prev => ({ ...prev, amount: undefined }))
                        }
                      }}
                      className={pickerFieldClass}
                    />
                    {linkedInvoice && (
                      <p className='text-xs text-[#6b7280]'>
                        {linkedInvoice.number ?? t('form.invoiceFallback')} ·{' '}
                        {formatCurrency(linkedInvoice.total, 'EUR', 2)} ·{' '}
                        {INVOICE_STATUS_LABELS[linkedInvoice.status] ??
                          linkedInvoice.status}
                      </p>
                    )}
                  </div>
                </SectionCard>
              )}

              {variant === 'inline' ? (
                <PaymentFormFields {...paymentFieldsProps} layout='invoice' />
              ) : (
                <PaymentSection
                  {...paymentFieldsProps}
                  index={hideContextFields ? 1 : 2}
                  title={t('form.payment')}
                />
              )}
            </>
          )}
          {isInterventionPayment && actionBar}
        </div>
      </div>

      {!isInterventionPayment && actionBar}
    </div>
  )
}

export function PaymentCreateDrawerContent(props: Props) {
  return <PaymentCreateFormCore {...props} />
}

type InvoicePaymentCreateFormProps = Omit<
  Props,
  'variant' | 'hideContextFields' | 'showStatusField' | 'showCancelAction'
>

export function InvoicePaymentCreateForm(props: InvoicePaymentCreateFormProps) {
  return (
    <PaymentCreateFormCore
      {...props}
      variant='inline'
      hideContextFields
      showStatusField={false}
      showCancelAction={false}
    />
  )
}

export default PaymentCreateDrawerContent
